/**
 * O SINGURA fereastra de adresa in toata aplicatia, deschisa de oriunde.
 *
 * Inainte, antetul, pagina de intrare, meniul, cosul si finalizarea montau
 * fiecare cate o `AddressSheet` — cinci `<dialog>`-uri in pagina, cu aceleasi
 * id-uri de campuri, dintre care unul singur deschis. Pe langa HTML-ul nevalid,
 * un script (sau un cititor de ecran) care cauta „campul de adresa" il gasea pe
 * cel inchis. Acum fereastra e una, iar paginile cer doar `open()`.
 *
 * `onSaved` se da la deschidere, nu la montare: doar apelantul stie ce urmeaza
 * dupa salvare (pagina de intrare a unei piete cu o singura vitrina intra in
 * meniu; restul raman pe loc).
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

import { AddressSheet } from "./AddressSheet";

interface OpenOptions {
  onSaved?: () => void;
}

interface AddressSheetContextValue {
  open: (options?: OpenOptions) => void;
  close: () => void;
}

const AddressSheetContext = createContext<AddressSheetContextValue | null>(null);

export function AddressSheetProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  // Callback-ul se tine in ref, nu in stare: nu are de ce sa produca o randare
  // si nu are voie sa fie apelat pentru o deschidere anterioara.
  const onSavedRef = useRef<(() => void) | undefined>(undefined);

  const open = useCallback((options?: OpenOptions) => {
    onSavedRef.current = options?.onSaved;
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    onSavedRef.current = undefined;
    setIsOpen(false);
  }, []);

  const value = useMemo<AddressSheetContextValue>(() => ({ open, close }), [open, close]);

  return (
    <AddressSheetContext.Provider value={value}>
      {children}
      <AddressSheet
        open={isOpen}
        onClose={() => close()}
        onSaved={() => {
          const callback = onSavedRef.current;
          onSavedRef.current = undefined;
          callback?.();
        }}
      />
    </AddressSheetContext.Provider>
  );
}

export function useAddressSheet(): AddressSheetContextValue {
  const value = useContext(AddressSheetContext);
  if (value === null) throw new Error("useAddressSheet a fost apelat in afara lui AddressSheetProvider.");
  return value;
}
